// Avantages validés par QR code (D-156) : le QR et le code à 6 chiffres se calculent sur le téléphone du membre, à partir
// de la clé reçue à l'ouverture de la fiche (hors ligne compris) ; la base refait le même calcul au moment de valider
// (migration 20261105800000_avantages_qr.sql : _qr_mac, _qr_code). HMAC-SHA256 (WebCrypto) du pas de 30 s :
//   signature du QR = 8 premiers octets de HMAC(clé, « q<pas> »), en hexadécimal ;
//   code = 4 premiers octets de HMAC(clé, « c<pas> ») lus en entier non signé, modulo 1 000 000, sur 6 chiffres.
// Le QR mène à <adresse du club>/v/<repère 16 hex><signature 16 hex>. Testé dans perk-qr.test.ts (vecteur partagé avec SQL).
import { withArticle } from './lien';

/** Le QR change toutes les 30 s. */
export const QR_PERIOD_S = 30;
/** La page ouverte par un scan reconnu garde la validation 2 minutes. */
export const SCAN_HOLD_MS = 120_000;
/** Le jeton d'un QR : le repère du passage (16 hex) puis la signature du pas (16 hex). */
export const TOKEN_RE = /^[0-9a-f]{32}$/;

/** Le pas de 30 s d'un instant (ms depuis 1970). */
export const stepOf = (ms: number, period = QR_PERIOD_S): number => Math.floor(ms / 1000 / period);
/** Les secondes avant le prochain QR (1 à 30). */
export const secondsLeft = (ms: number, period = QR_PERIOD_S): number => period - (Math.floor(ms / 1000) % period);

export function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}
export const bytesToHex = (b: Uint8Array): string => [...b].map(x => x.toString(16).padStart(2, '0')).join('');

/** La clé du passage, prête pour WebCrypto (non exportable). */
export async function importQrKey(hex: string): Promise<CryptoKey> {
  const raw = hexToBytes(hex);
  return crypto.subtle.importKey('raw', raw.buffer.slice(raw.byteOffset, raw.byteOffset + raw.byteLength) as ArrayBuffer,
    { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
}
async function hmac(key: CryptoKey, msg: string): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(msg)));
}
/** La signature du QR pour un pas (16 hex). */
export async function qrMac(key: CryptoKey, step: number): Promise<string> {
  return bytesToHex((await hmac(key, `q${step}`)).slice(0, 8));
}
/** Le code à 6 chiffres pour un pas. */
export async function qrCode(key: CryptoKey, step: number): Promise<string> {
  const h = await hmac(key, `c${step}`);
  const n = ((h[0]! << 24) >>> 0) + (h[1]! << 16) + (h[2]! << 8) + h[3]!;
  return String(n % 1_000_000).padStart(6, '0');
}
/** Le QR et le code d'un pas. */
export async function qrFor(key: CryptoKey, ref: string, step: number): Promise<{ token: string; code: string }> {
  const [mac, code] = await Promise.all([qrMac(key, step), qrCode(key, step)]);
  return { token: `${ref}${mac}`, code };
}
/** L'adresse ouverte par l'appareil photo du partenaire. */
export const qrUrl = (origin: string, token: string): string => `${origin.replace(/\/+$/, '')}/v/${token}`;
/** « 482 913 ». */
export const codeSpaced = (c: string): string => (c.length === 6 ? `${c.slice(0, 3)} ${c.slice(3)}` : c);
/** Six chiffres, espaces et tirets retirés ; sinon null. */
export const cleanCode = (s: string): string | null => { const d = s.replace(/[\s\-–.]/g, ''); return /^\d{6}$/.test(d) ? d : null; };

// --- Le suivi -------------------------------------------------------------------------------------------------------
export type QrMethod = 'qr' | 'code' | 'hold';
export type QrVia = 'partner' | 'link' | 'bureau' | 'benevole';
export const HOW_LABEL: Record<QrMethod, string> = { qr: 'QR scanné', code: 'Code tapé', hold: 'Montré à l’écran' };

/** Qui a validé, vu du partenaire (vouvoiement, jamais le nom du membre) : « par vous (Jean) », « par Paul », « par votre
 *  lien privé », « sans scan (appui de 5 s) ». */
export function partnerWho(u: { method: QrMethod; via: QrVia | null; validator: string | null; mine: boolean }): string {
  if (u.method === 'hold') return 'sans scan (appui de 5 s)';
  if (u.via === 'link') return 'par votre lien privé';
  if (u.mine) return u.validator ? `par vous (${u.validator})` : 'par vous';
  return u.validator ? `par ${u.validator}` : 'par votre compte';
}

/** Où et par qui, vu du bureau : « Garage Saint-Jean · validé par Jean », « Le club · validé par Karim, à la buvette »,
 *  « Boulangerie Martin · son lien privé », « Garage Saint-Jean · personne n’a scanné ». */
export function bureauWhere(u: { partner_name: string | null; method: QrMethod; via: QrVia | null; validator: string | null; post: string | null }): string {
  const where = u.partner_name ?? 'Le club';
  if (u.method === 'hold') return `${where} · personne n’a scanné`;
  if (u.via === 'link') return `${where} · son lien privé`;
  const who = u.validator ? `validé par ${u.validator}` : 'validé';
  if (u.via === 'benevole' && u.post) return `${where} · ${who}, ${lowerPost(u.post)}`;
  if (u.via === 'bureau') return `${where} · ${who} (bureau)`;
  return `${where} · ${who}`;
}
/** « du BC Rivebelle », « de l’ASC Valdorne », « de la Union… », « de Lou Basket ». */
export function ofClubName(club: string): string {
  const a = withArticle(club);
  return a.startsWith('le ') ? `du ${a.slice(3)}` : a.startsWith('la ') ? `de la ${a.slice(3)}` : `de ${a}`;
}

/** « à la buvette », « à l’accueil », « au chrono », « aux gâteaux » ; un poste inconnu : « poste Table de marque ». */
export function lowerPost(post: string): string {
  const p = post.trim();
  const low = p.charAt(0).toLowerCase() + p.slice(1);
  if (/^(buvette|caisse|table|cuisine|billetterie|sono|e-marque)\b/i.test(p)) return `à la ${low}`;
  if (/^(accueil|arbitrage|installation|entrée|affichage)\b/i.test(p)) return `à l’${low}`;
  if (/^(chrono|rangement|lavage|bar|vestiaire|parking|stand)\b/i.test(p)) return `au ${low}`;
  if (/^(gâteaux|maillots|goûters)\b/i.test(p)) return `aux ${low}`;
  return `poste ${p}`;
}
